import React, { useEffect, useState } from 'react';
import { 
    Container, Typography, Button, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, Paper, IconButton, 
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Box
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import { API } from '../core/API';

const adminApi = new API("/api/v1/admin");

const Admins = () => {
    const [admins, setAdmins] = useState([]);
    const [open, setOpen] = useState(false);
    const [editingAdmin, setEditingAdmin] = useState(null);
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        fullName: ''
    });

    useEffect(() => {
        loadAdmins();
    }, []);

    const loadAdmins = async () => {
        try {
            const result = await adminApi.get("/");
            setAdmins(result.data || []);
        } catch (error) {
            console.error(error);
        }
    };

    const handleOpen = (admin = null) => {
        if (admin) {
            setEditingAdmin(admin);
            setFormData({
                username: admin.username,
                password: '', // Don't show password
                fullName: admin.fullName
            });
        } else {
            setEditingAdmin(null);
            setFormData({
                username: '',
                password: '',
                fullName: ''
            });
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleSubmit = async () => {
        try {
            if (editingAdmin) {
                // If password is empty, don't send it to avoid overwriting with hashed empty string
                const data = { ...formData };
                if (!data.password) delete data.password;
                await adminApi.put(`/${editingAdmin._id}`, data);
            } else {
                await adminApi.put("/", formData);
            }
            loadAdmins();
            handleClose();
        } catch (error) {
            console.error(error);
            alert(error.message);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this admin?')) {
            try {
                await adminApi.delete(`/${id}`);
                loadAdmins();
            } catch (error) {
                console.error(error);
            }
        }
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">System Administrators</Typography>
                <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
                    Add Admin
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Full Name</TableCell>
                            <TableCell>Username</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {admins.map((admin) => (
                            <TableRow key={admin._id}>
                                <TableCell>{admin.fullName}</TableCell>
                                <TableCell>{admin.username}</TableCell>
                                <TableCell align="right">
                                    <IconButton onClick={() => handleOpen(admin)} color="primary">
                                        <Edit />
                                    </IconButton>
                                    <IconButton onClick={() => handleDelete(admin._id)} color="error">
                                        <Delete />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{editingAdmin ? 'Edit Admin' : 'New Admin'}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Full Name"
                        fullWidth
                        variant="outlined"
                        value={formData.fullName}
                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                        sx={{ mb: 2, mt: 1 }}
                    />
                    <TextField
                        margin="dense"
                        label="Username"
                        fullWidth
                        variant="outlined"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase() })}
                        disabled={!!editingAdmin}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Password"
                        fullWidth
                        variant="outlined"
                        type="password"
                        placeholder={editingAdmin ? "Leave blank to keep current" : "Enter password"}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained">
                        {editingAdmin ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Admins;
